<template>
  <div id="panelChart9" class="panel panel-default">
    <add-representatives
      v-if="showAddModal"
      :closeAction="closeAddModal"
      :representatives="representatives"
      :accountId="accountId"
      @representativeAdded="refreshRepresentatives"/>
    <div class="panel-title top-margin">Authorized representatives</div>

    <div class="line-separator"></div>

    <div class="row" v-if="representatives.length === 0">
      <div class="col-lg-12">
        <div class="alert alert-info alert-dismissible" role="alert">
          No authorized representatives found.
        </div>
      </div>
    </div>

    <div v-if="representatives.length > 0"
      v-bind:class="{'table-fixed-header--md cols-4': representatives.length > 6, 'table-responsive': true, 'bottom-margin': true}">

      <table class="table table-striped">
        <thead>
          <tr>
            <th class="text-left">Name</th>

            <th class="text-center">User type</th>

            <th class="text-center">Primary user</th>

            <th class="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(representative, index) in representatives" :key="index">
            <td class="text-left">
              <a
                class="account-representative"
                v-on:click="goToUserView(representative.id, representative.userType, representative.firstName)"
              >{{ representative.fullName }}</a>
            </td>

            <td class="text-center">{{ getUserTypeLabel(representative.userType) }}</td>

            <td class="text-center">
              <div
                v-if="canRepresentativeBePrimary(representative.userType)"
                :class="{ 'radio-item': true, 'radio-item-unselected': !(representative.id === primaryInvestorId) } "
                :style="{ pointerEvents: isReadOnly ? 'none' : null, opacity: isReadOnly ? 0.5 : 1 }"
                @click="attemptUpdatePrimaryInvestor(representative)"
              >
                <input type="radio"  :value="true" readonly/>
                <label />
              </div>
            </td>

            <td class="text-center">
              <button v-if="isDeleteEnabled" @click="attemptRepresentativeDelete(representative)" type="button" class="btn btn-trash">
                <em class="fa fa-trash fa-2x delete-icon"></em>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

    </div>
    <button :disabled="isReadOnly" class="btn btn-default btn-lg btn-add-representative" @click="openAddModal">Add new representative</button>
    <div v-if="isIndividualAccount" class="panel-body account-notice">
      <div class="alert alert-info alert-dismissible mt-lg" role="alert">
        <p><em class="icon-info pr-sm"></em>The primary investor for an INDIVIDUAL account cannot be changed</p>
      </div>
    </div>
  </div>
</template>

<script>
  import { getUserTypeLabel } from '@/util';
  import { UserTypes } from '@/data/static/userTypes';
  import { Events } from '@/data/static/events';
  import { AccountTypes } from '@/data/static/accountTypes';
  import { Constants } from '@/data/static/constants';
  import {
    HIDE_QUESTION_MODAL,
    SHOW_QUESTION_MODAL,
    SHOW_ERRORS_MODAL,
    SET_ERRORS_MODAL_INFO,
  } from '@/store/mutation-types';

  import {
    mapMutations,
    mapActions,
  } from 'vuex';

  import AddRepresentatives from './modals/AddRepresentatives';

const mix = {
    methods: {
      getUserTypeLabel,
    },
  };

  export default {
    name: 'AccountRepresentatives',
    data() {
      return {
        showAddModal: false,
      };
    },
    components: {
      'add-representatives': AddRepresentatives,
    },
    mixins: [mix],
    props: {
      accountId: {
        type: [Number, String],
        required: false,
        default: 0,
      },
      representatives: {
        type: Array,
        required: true,
        default: [],
      },
      accountName: {
        type: String,
        required: true,
        default: '',
      },
      accountType: {
        type: String,
        required: true,
      },
      primaryInvestorId: {
        type: Number,
        required: true,
      },
    },
    computed: {
      isDeleteEnabled() {
        return this.representatives.length > 1 && !this.isReadOnly;
      },
      accountTypeName() {
        return this.accountType.toUpperCase();
      },
      isIndividualAccount() {
        return this.accountTypeName === AccountTypes.INDIVIDUAL.name;
      },
      isReadOnly() {
        const app = this.$store && this.$store.state && this.$store.state.app;
        return !!(app && app.user && app.user.userType === 'READ_ONLY');
      },
    },
    methods: {
      canRepresentativeBePrimary(userType = '') {
        if (
          this.accountTypeName !== AccountTypes.TRUST.name &&
          this.accountTypeName !== AccountTypes.INSTITUTION.name
        ) {
          return (
            userType !== UserTypes.ADVISOR &&
            userType !== UserTypes.INSTITUTIONAL_REPRESENTATIVE.name &&
            userType !== UserTypes.READ_ONLY.name
          );
        }
        return userType !== UserTypes.ADVISOR && userType !== UserTypes.READ_ONLY.name;
      },
      attemptRepresentativeDelete(representative) {
        if (representative.id === this.primaryInvestorId) {
          this.setErrorsModalInfo(['You cannot delete the primary investor for the account. Please select another investor to be primary and then try again']);
          this.showErrorsModal();
        } else {
          this.showConfirmationModal(representative);
        }
      },
      showConfirmationModal(representative) {
        const isDefaultAccount = this.representatives
          .find(rep => rep.id === representative.id && rep.defaultAccountId === this.accountId);
        const question = isDefaultAccount
          ? 'This is the default login account for this user, if removed, their INDIVIDUAL ACCOUNT will be set as the default.'
          : `Are you sure you want to unlink ${representative.fullName} from the account ${this.accountName}?`;
        this.showQuestionModal({
          question,
          title: 'Unlink representative',
          method: () => {
            this.deleteRepresentative(representative, isDefaultAccount);
            this.hideQuestionModal();
          },
          yesButtonText: 'Confirm',
          hideCancelButton: false,
        });
      },
      attemptUpdatePrimaryInvestor(representative) {
        if (
            !(representative.id === this.primaryInvestorId) &&
            this.accountType !== AccountTypes.INDIVIDUAL.name) {
          this.showPrimaryInvestorConfirmation(representative);
        }
      },
      showPrimaryInvestorConfirmation(representative) {
        const question = this.accountType === AccountTypes.INSTITUTION.name
          ? Constants.updatePrimaryInvestorMessage.institutionAccountMessage
          : Constants.updatePrimaryInvestorMessage.notInstitutionAccountMessage;
        this.showQuestionModal({
          question,
          title: `Are you sure you want to select ${representative.fullName} as the primary investor for the account ${this.accountName}?`,
          method: () => {
            this.updatePrimaryInvestor(representative.id);
            this.hideQuestionModal();
          },
          yesButtonText: 'Confirm',
          hideCancelButton: false,
        });
      },
      async updatePrimaryInvestor(id) {
        this.$emit(Events.REPRESENTATIVE_LOADING_STARTED);
        await this.updateAccountPrimaryInvestor({
          accountId: this.accountId, userId: id,
        });
        this.$emit(Events.REPRESENTATIVE_LOADING_SUCCEED);
        this.$emit(Events.REPRESENTATIVES_UPDATED);
      },
      async deleteRepresentative(representative, isDefaultAccount) {
        if (!isDefaultAccount) {
          this.$emit(Events.REPRESENTATIVE_LOADING_STARTED);
        }
        await this.deleteAccountUser({
          accountId: this.accountId, userId: representative.id,
        });

        if (isDefaultAccount) {
          const params = { name: representative.firstName, userId: representative.id };
          let routeParams;
          if (representative.userType === UserTypes.FINANCIAL_ADVISOR.name) {
            routeParams = { name: 'EditFinancialAdvisor', params };
          } else {
            routeParams = { name: 'UserDetail', params };
          }
          this.$router.push(routeParams);
        } else {
          this.$emit(Events.REPRESENTATIVE_LOADING_SUCCEED);
          this.$emit(Events.REPRESENTATIVES_UPDATED);
        }
      },
      ...mapMutations('question', {
        hideQuestionModal: HIDE_QUESTION_MODAL,
        showQuestionModal: SHOW_QUESTION_MODAL,
      }),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapActions('account', [
        'deleteAccountUser',
        'updateAccountPrimaryInvestor',
      ]),
      openAddModal() {
        this.showAddModal = true;
      },
      closeAddModal() {
        this.showAddModal = false;
      },
      refreshRepresentatives() {
        this.$emit(Events.REPRESENTATIVES_UPDATED);
      },
      goToUserView(userId, type, name) {
        const routeParams = {
          name:
          type === UserTypes.FINANCIAL_ADVISOR.name
            ? 'EditFinancialAdvisor'
            : 'UserDetail',
          params: { name, userId },
        };
        this.$router.push(routeParams);
      },
    },
  };
</script>

<style scoped>
  .panel-title {
    font-size: 20px;
    margin-bottom: 5px;
  }
  .btn-trash {
    border: 1px solid #DCE0E5;
    background: white;
  }
  .delete-icon {
    color: #f05050;
    margin-top: -3px;
  }
  .radio-item label {
    color: #666;
    font-weight: normal;
    padding-left: 25px;
    padding-top: 5px;
    cursor: pointer;
  }
  .radio-item {
    height: 25px;
  }
  .radio-item label:before {
    content: " ";
    display: inline-block;
    position: absolute;
    top: 0;
    left: 0px;
    margin: 0 5px 0 0;
    width: 20px;
    height: 20px;
    border-radius: 11px;
    background-color: transparent;
  }
  .radio-item-unselected label:before {
    border: 2px solid #d3d3d3;
  }
  .radio-item label:after {
    border-radius: 5px;
    width: 10px;
    height: 10px;
    position: absolute;
    top: 5px;
    left: 5px;
    margin: auto;
    content: "";
    display: block;
    background-color: #127285;
  }
  .radio-item.radio-item-unselected label:after {
    display: none;
  }
  table {
    border-collapse: collapse;
  }
  th {
    padding: 14px 8px;
    color: #888;
    border-bottom-width: 1px;
    vertical-align: bottom;
    border-bottom: 1px solid #eee;
    border-top: 0;
    line-height: 1.52857143;
    font-weight: normal;
  }
  td {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
    height: 60px;
  }
  .table-striped>tbody>tr:nth-of-type(odd) {
    background-color: #fafbfc;
  }
  .panel-title {
    font-size: 20px;
  }
  .panel-default {
    padding: 30px 40px;
  }
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  tbody tr td:last-child {
    padding-top: 10px;
  }

  .btn-add-representative {
    margin-top: 15px;
  }

  .account-notice {
    padding: 0;
  }

  .account-representative {
    color: #00788D;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// AccountRepresentatives.vue?5509e134